/**
 * Tiny promise wrapper over one IndexedDB object store — the offline outbox.
 * IndexedDB (not localStorage) because entries carry request bodies of any
 * size and must survive tab closes and app restarts until they sync.
 */

const DB_NAME = 'tt-offline'
const STORE = 'outbox'
const VERSION = 1

export interface OutboxEntry {
  id: string
  method: 'POST' | 'PUT' | 'PATCH'
  url: string
  body: unknown
  /** `${userId}:${storeId}` — entries only replay into the session that made them. */
  scope: string
  createdAt: number
  status: 'pending' | 'failed'
  /** Server message from the last failed replay. */
  error?: string
  attempts: number
}

let dbPromise: Promise<IDBDatabase> | null = null

const open = (): Promise<IDBDatabase> => {
  if (!('indexedDB' in globalThis)) return Promise.reject(new Error('IndexedDB unavailable'))
  dbPromise ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION)
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: 'id' })
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => {
      dbPromise = null
      reject(req.error)
    }
  })
  return dbPromise
}

const run = async <T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
  const db = await open()
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode)
    const req = work(tx.objectStore(STORE))
    tx.oncomplete = () => resolve(req.result)
    tx.onerror = () => reject(tx.error)
    tx.onabort = () => reject(tx.error)
  })
}

export const outboxDb = {
  all: (): Promise<OutboxEntry[]> => run<OutboxEntry[]>('readonly', s => s.getAll()).then(rows => rows.sort((a, b) => a.createdAt - b.createdAt)),
  put: (entry: OutboxEntry): Promise<IDBValidKey> => run('readwrite', s => s.put(entry)),
  remove: (id: string): Promise<undefined> => run('readwrite', s => s.delete(id)),
}
